import React, { FC, useEffect, useState } from 'react';
import { TextField, Popover, Button, InputAdornment, IconButton, Grid } from '@material-ui/core';
import { LocalizationProvider, DateRange, MobileDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@material-ui/pickers/adapter/date-fns';
import { getLocale } from '../../utils';
import { useAppData } from '../../providers';
import { CalendarIcon } from '../CustomIcons';
import { useStyles } from './styles';
import { useCommonStyles } from '../../styles';

interface Props {
  defaultValue: DateRange<Date>;
  translation: any;
  onRangeChange: (monthRange: DateRange<Date>) => void;
}

export const MonthRangePicker: FC<Props> = ({ defaultValue, translation, onRangeChange }) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { locale } = useAppData();
  const [localeString, setLocaleString] = useState<string>('en');
  const [range, setRange] = useState<DateRange<Date>>(defaultValue);
  const [temp, setTemp] = useState<DateRange<Date>>(defaultValue);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [openFrom, setOpenFrom] = useState<boolean>(false);
  const [openTo, setOpenTo] = useState<boolean>(false);
  const [showDateError, setShowDateError] = useState<boolean>(false);

  useEffect(() => {
    setRange(defaultValue);
    setTemp(defaultValue);
  }, [defaultValue]);

  const applyRange = () => {
    if (temp[0] !== null && temp[1] !== null && temp[0] > temp[1]) {
      setShowDateError(true);
    } else {
      setShowDateError(false);
      setRange(temp);
      setOpen(false);
      setAnchorEl(null);
      onRangeChange(temp);
    }
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const label = range[0] && range[1] ? `${range[0].toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} – ${range[1].toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : 'Select month range';

  useEffect(() => {
    if (locale) {
      setLocaleString(locale);
    }
  }, [locale]);

  return (
    <LocalizationProvider dateAdapter={DateFnsUtils} locale={getLocale(localeString)}>
      <TextField
        label=""
        value={label}
        variant="outlined"
        onClick={handleOpen}
        fullWidth
        className={classes.root}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={(event) => {
                  if (open) {
                    setOpen(false);
                    setAnchorEl(null);
                  } else {
                    setAnchorEl(event.currentTarget);
                    setOpen(true);
                  }
                }}
              >
                <CalendarIcon />
              </IconButton>
            </InputAdornment>
          ),
          inputProps: { readOnly: true },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setOpen(false);
          setTemp(defaultValue);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Grid container direction="column" justify="space-between" className={classes.paper}>
          <Grid container justify="space-between" className={classes.pickers}>
            <Grid item className={classes.datepiciker}>
              <p className={commonClasses.formControlTitle}>{translation.text_6303}</p>
              <MobileDatePicker
                showToolbar={false}
                className={classes.picker}
                open={openFrom}
                views={['month', 'year']}
                value={temp[0]}
                onOpen={() => {
                  setOpenFrom(true);
                }}
                onClose={() => {
                  setOpenFrom(false);
                }}
                onChange={(date: Date | null) => setTemp([date, temp[1]])}
                renderInput={(props) => {
                  const { value: startValue, ...restStartProps } = props.inputProps || {};
                  return (
                    <TextField
                      {...props}
                      className={classes.root}
                      variant="outlined"
                      helperText={null}
                      // onClick={() => {
                      //   setOpenFrom(!openFrom);
                      // }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                if (openFrom) {
                                  setOpenFrom(false);
                                } else {
                                  setOpenFrom(true);
                                }
                              }}
                            >
                              <CalendarIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        inputProps: { ...restStartProps, readOnly: true },
                      }}
                      value={`${startValue || 'From'}`}
                    />
                  );
                }}
              />
            </Grid>
            <Grid item className={classes.datepiciker}>
              <p className={commonClasses.formControlTitle}>{translation.text_6304}:</p>
              <MobileDatePicker
                showToolbar={false}
                className={classes.picker}
                open={openTo}
                openPickerIcon={<CalendarIcon />}
                views={['month', 'year']}
                minDate={temp[0] || undefined} // prevent invalid ranges
                value={temp[1]}
                onOpen={() => {
                  setOpenTo(true);
                }}
                onClose={() => {
                  setOpenTo(false);
                }}
                onChange={(date: Date | null) => {
                  if (temp[0] !== null && temp[0]?.getMonth() === date?.getMonth() && temp[0]?.getFullYear() === date?.getFullYear()) {
                    date = new Date(date?.getFullYear(), date?.getMonth(), date?.getDate() + 1);
                  }
                  setTemp([temp[0], date]);
                }}
                renderInput={(props) => {
                  const { value: endValue, ...restEndProps } = props.inputProps || {};
                  return (
                    <TextField
                      {...props}
                      className={classes.root}
                      variant="outlined"
                      helperText={null}
                      onClick={() => {
                        setOpenTo(!openTo);
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                if (openTo) {
                                  setOpenTo(false);
                                } else {
                                  setOpenTo(true);
                                }
                              }}
                            >
                              <CalendarIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        inputProps: { ...restEndProps, readOnly: true },
                      }}
                      value={`${endValue || 'To'}`}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
          {showDateError && (
            <Grid item className={classes.errorMessage}>
              {translation.text_6305}
            </Grid>
          )}

          <Grid container direction="row" justify="space-between">
            <Button className={commonClasses.activeDialogBtn} onClick={applyRange}>
              {translation.text_65}
            </Button>
          </Grid>
        </Grid>
      </Popover>
    </LocalizationProvider>
  );
};
